import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ZohoProvider, GdriveProvider, PeopleProvider, ThemeProvider, useAuth } from './context';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

import Login          from './components/Login/Login';
import Redirect       from './components/Redirect/Redirect';
import Mailer         from './components/Mailer/Mailer';
import Finances       from './components/Finances/Finances';
import AccessTokenRedirect from './components/AccessTokenRedirect/AccessTokenRedirect';
import MagicLogin     from './components/MagicLogin/MagicLogin';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import SalesPage      from './components/Sales/SalesPage';
import ManagerPage    from './components/Manager/ManagerPage';
import PrivateSpacePage from './components/PrivateSpace/PrivateSpacePage';
import CalendarPage    from './components/Calendar/CalendarPage';

// Admin portal (nested, link-based)
import AdminLayout       from './components/AdminLayout/AdminLayout';
import ClientsListPage   from './components/workspace/ClientsListPage';
import MyWorkPage        from './components/workspace/MyWorkPage';
import ProjectsPage      from './components/workspace/ProjectsPage';
import MyTasksPage       from './components/workspace/MyTasksPage';
import MessagesPage      from './components/workspace/MessagesPage';
import ClientWorkspace   from './components/workspace/ClientWorkspace';
import ClientWorkspaceOverview from './components/workspace/ClientWorkspaceOverview';
import MessageBoard      from './components/workspace/MessageBoard';
import TodoBoard         from './components/workspace/TodoBoard';
import ClientProjectsList from './components/workspace/ClientProjectsList';
import ClientFilesRequestsPage from './components/workspace/ClientFilesRequestsPage';
import ProjectPage       from './components/workspace/ProjectPage';

// Client portal (nested, link-based)
import ClientLayout       from './components/client/ClientLayout';
import ClientOverview     from './components/client/ClientOverview';
import ClientProjectsPage from './components/client/ClientProjectsPage';
import ClientProjectPage  from './components/client/ClientProjectPage';
import ClientFilesPage    from './components/client/ClientFilesPage';
import PingWorkspace      from './components/Pings/PingWorkspace';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'client' ? '/client-portal' : '/portal'} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ZohoProvider>
          <GdriveProvider>
        <Router>
          <PeopleProvider>
          <Routes>
            {/* Public */}
            <Route path="/login"           element={<Login />} />
            <Route path="/redirect"        element={<Redirect />} />
            <Route path="/accessRedirect"  element={<AccessTokenRedirect />} />
            <Route path="/magic-login"     element={<MagicLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Admin portal — nested, unique URLs per client & project */}
            <Route path="/portal" element={<ProtectedRoute requiredRoles={['admin', 'project_manager', 'production']}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<MyWorkPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="clients" element={<ClientsListPage />} />
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="pings" element={<PingWorkspace />} />
              <Route path="manager" element={<ProtectedRoute requiredRole="admin"><ManagerPage /></ProtectedRoute>} />
              <Route path="private-space" element={<PrivateSpacePage />} />
              <Route path="private-space/:folderId" element={<PrivateSpacePage />} />
              <Route path="clients/:clientId" element={<ClientWorkspace />}>
                <Route index element={<ClientWorkspaceOverview />} />
                <Route path="board"    element={<MessageBoard />} />
                <Route path="todos"    element={<TodoBoard />} />
                <Route path="projects" element={<ClientProjectsList />} />
                <Route path="files" element={<ClientFilesRequestsPage />} />
                <Route path="projects/:projectId" element={<ProjectPage />} />
              </Route>
            </Route>

            {/* Mailer & Finances — admin only (unchanged) */}
            <Route path="/mailer" element={<ProtectedRoute requiredRole="admin"><Mailer /></ProtectedRoute>} />
            <Route path="/finances" element={<ProtectedRoute requiredRole="admin"><Finances /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute requiredRole="admin"><CalendarPage /></ProtectedRoute>} />
            <Route path="/sales" element={<ProtectedRoute requiredRoles={['admin', 'project_manager']}><SalesPage /></ProtectedRoute>} />

            {/* Client portal — nested, unique URLs per project */}
            <Route path="/client-portal" element={<ProtectedRoute requiredRole="client"><ClientLayout /></ProtectedRoute>}>
              <Route index element={<ClientOverview />} />
              <Route path="board"    element={<MessageBoard />} />
              <Route path="projects" element={<ClientProjectsPage />} />
              <Route path="projects/:projectId" element={<ClientProjectPage />} />
              <Route path="files" element={<ClientFilesPage />} />
            </Route>

            {/* Root: redirect based on auth state */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          </PeopleProvider>
        </Router>
          </GdriveProvider>
        </ZohoProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
